"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/app/_module/lib/utils";

interface AudioWaveformProps {
  /** Current playback time in seconds */
  currentTime: number;
  /** Total duration in seconds */
  duration: number;
  /** Whether audio is currently playing */
  isPlaying: boolean;
  /** Callback when user clicks on waveform to seek */
  onSeek: (time: number) => void;
  /** Number of bars to display */
  barCount?: number;
  /** Whether the audio is still loading */
  isLoading?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * AudioWaveform - A CSS-based audio waveform visualization component
 *
 * Features:
 * - Lightweight, no external dependencies
 * - Animated bars that pulse during playback
 * - Clickable for seeking
 * - Color-coded (played vs remaining)
 * - Responsive design
 */
export const AudioWaveform: React.FC<AudioWaveformProps> = ({
  currentTime,
  duration,
  isPlaying,
  onSeek,
  barCount = 50,
  isLoading = false,
  className,
}) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const [hoveredPosition, setHoveredPosition] = useState<number | null>(null);

  // Generate random heights for bars (cached)
  const [barHeights] = useState(() => {
    // Create a semi-random pattern that looks like a natural waveform
    return Array.from({ length: barCount }, (_, i) => {
      // Use sine waves with different frequencies for natural variation
      const position = i / barCount;
      const wave1 = Math.sin(position * Math.PI * 2) * 0.3;
      const wave2 = Math.sin(position * Math.PI * 4 + 0.5) * 0.2;
      const wave3 = Math.sin(position * Math.PI * 8 + 1) * 0.15;
      const noise = (Math.sin(i * 12.9898) * 43758.5453) % 1 * 0.35;

      // Combine waves and normalize to 0.3-1.0 range
      const height = 0.3 + Math.abs(wave1 + wave2 + wave3 + noise);
      return Math.min(1, height);
    });
  });

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!waveformRef.current || isLoading || !duration) return;

    const rect = waveformRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    onSeek(newTime);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!waveformRef.current || !duration) return;

    const rect = waveformRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;

    setHoveredPosition(percentage);
  };

  const handleMouseLeave = () => {
    setHoveredPosition(null);
  };

  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <div className={cn("relative", className)}>
      <div
        ref={waveformRef}
        className={cn(
          "flex items-center justify-between gap-[2px] h-12 px-1",
          isLoading ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          "transition-opacity duration-200"
        )}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {barHeights.map((height, index) => {
          const barPosition = index / barCount;
          const isPlayed = barPosition <= progress;
          const isHovered = hoveredPosition !== null && barPosition <= hoveredPosition;

          return (
            <div
              key={index}
              className={cn(
                "flex-1 rounded-full transition-all duration-150 min-w-[2px]",
                isPlayed
                  ? "bg-blue-600 dark:bg-blue-500"
                  : isHovered
                  ? "bg-blue-400 dark:bg-blue-400"
                  : "bg-gray-300 dark:bg-gray-600",
                isPlaying && isPlayed && "animate-pulse-subtle"
              )}
              style={{
                height: `${height * 100}%`,
                animationDelay: `${index * 20}ms`,
              }}
            />
          );
        })}
      </div>

      {/* Hover time indicator */}
      {hoveredPosition !== null && !isLoading && duration > 0 && (
        <div
          className="absolute top-0 -translate-y-6 bg-gray-900 text-white text-xs px-2 py-1 rounded pointer-events-none"
          style={{
            left: `${hoveredPosition * 100}%`,
            transform: `translateX(-50%) translateY(-100%)`,
          }}
        >
          {formatTime(hoveredPosition * duration)}
        </div>
      )}
    </div>
  );
};

// Helper function to format time
function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
